import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import { listAllPhotos } from '../../db/journal.js';
import { JournalDay } from './JournalDay.js';
import { DayNotes } from './DayNotes.js';
import type { NoteEventPreset } from './DayNotes.js';
import { WeekReview } from './WeekReview.js';
import { WeightTrendTile } from './WeightTrendTile.js';
import { MeasurementTrendTile } from './MeasurementTrendTile.js';
import { useIsChild } from '../../hooks/useModules.js';
import { Achievements } from '../Achievements.js';
import { WeekRings } from '../WeekRings.js';
import { PhotoImage } from './JournalPhotos.js';
import { useToday } from '../../hooks/useToday.js';
import { MonthView } from './MonthView.js';
import { DayStory } from './DayStory.js';
import { BucketList } from './BucketList.js';
import { JournalDays } from './JournalDays.js';
import type { DayFocus } from './JournalDays.js';
import { formatDay } from '../../format.js';
import { SwipePages, neighbourIn, useSwipeBack } from '../../gestures/SwipeProvider.js';
import { addDays } from '@kraftwerk/shared';
import { SealedNotice } from '../SealedNotice.js';
import type { IsoDate } from '@kraftwerk/shared';

/** Von außen gewünscht: `week`/`photos` sind seit 18.10.2026 Teil des Rückblicks. */
export type JournalView = 'days' | 'month' | 'bucket' | 'review' | 'photos' | 'week';
type View = 'days' | 'month' | 'bucket' | 'review';

function viewOf(requested: JournalView): View {
  return requested === 'week' || requested === 'photos' ? 'review' : requested;
}

/**
 * Der Reiter „Tagebuch": nachlesen, nachtragen, Fotos durchsehen.
 *
 * Bis hierher gab es nur den heutigen Tag — was man vorgestern eingetragen
 * hatte, war nirgends zu sehen. Ein Tagebuch, das man nicht lesen kann, ist ein
 * Formular.
 */
const JOURNAL_VIEWS = ['days', 'month', 'bucket', 'review'] as const;

export function JournalScreen({
  openDate,
  onDateChange,
  requestedView = null,
  onViewShown,
  notePreset = null,
  onNotePresetUsed,
}: {
  /** Von außen geöffneter Tag, etwa aus „Mehr eintragen" auf Heute. */
  openDate: string | null;
  onDateChange: (date: string | null) => void;
  /** Von außen gewünschte Ansicht, etwa „Woche" aus der Sonntags-Mitteilung. */
  requestedView?: JournalView | null;
  onViewShown?: () => void;
  /** Aus dem Kalender: Gedanken zu diesem Termin schreiben. */
  notePreset?: NoteEventPreset | null;
  onNotePresetUsed?: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const child = useIsChild();
  const [view, setView] = useState<View>(requestedView ? viewOf(requestedView) : 'days');

  useEffect(() => {
    if (!requestedView) return;
    setView(viewOf(requestedView));
    onViewShown?.();
  }, [requestedView, onViewShown]);
  const todayDate = useToday();
  // Aus der Heute-Karte: gleich zum Befinden, zu einem neuen Gedanken oder
  // zu den Fotos — gilt nur für das eine Öffnen.
  const [focus, setFocus] = useState<DayFocus | null>(null);
  const open = (date: string | null, next: DayFocus | null = null) => {
    setFocus(next);
    onDateChange(date);
  };

  useEffect(() => {
    if (!openDate || (focus !== 'photo' && focus !== 'note')) return;
    // Nach dem Zeichnen: Der neue Gedanke steht unter dem Befinden.
    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(
        focus === 'photo' ? 'journal-day-photos' : 'journal-day-notes',
      );
      // `scrollIntoView` gibt es nicht überall (jsdom) — dann eben ohne Sprung.
      if (typeof target?.scrollIntoView === 'function') target.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [focus, openDate]);

  if (openDate) {
    return (
      <section aria-labelledby="journal-day-heading" className="flex flex-col gap-3">
        {/* Zurück und Datum stehen fest — nur der Tag darunter gleitet. */}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => open(null)}
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
          >
            {t('journal.back')}
          </button>
          <h2 id="journal-day-heading" className="kw-kicker">
            {openDate === todayDate
              ? t('journal.today')
              : formatDay(openDate, 'full', i18n.language)}
          </h2>
        </div>
        <JournalDayDetail
          date={openDate}
          today={todayDate}
          onOpen={(date) => open(date)}
          render={(day) => (
            <>
              <DayStory date={day} />
              <div className="kw-tile">
                <JournalDay date={day} startEditing={day === openDate && focus === 'checkin'} />
              </div>
              <DayNotes
                date={day}
                preset={day === openDate ? notePreset : null}
                onPresetUsed={onNotePresetUsed}
                startNew={day === openDate && focus === 'note'}
              />
            </>
          )}
        />
      </section>
    );
  }

  // Tage · Monat · Rückblick (18.10.2026) — der Rückblick fasst Woche und
  // Fotos zusammen. Kinder (E16): keine Woche mit Gewicht, Protein und Ringen.
  const tabs: { key: View; label: string }[] = [
    { key: 'days', label: t('journal.tabs.days') },
    { key: 'month', label: t('journal.tabs.month') },
    { key: 'bucket', label: t('journal.tabs.bucket') },
    { key: 'review', label: t('journal.tabs.review') },
  ];

  return (
    <div className="flex flex-col gap-3">
      <nav aria-label={t('journal.tabs.label')}>
        <ul className="flex gap-1 rounded-full border border-line bg-surface p-1">
          {tabs.map((tab) => (
            <li key={tab.key} className="flex-1">
              <button
                type="button"
                aria-current={view === tab.key ? 'page' : undefined}
                onClick={() => setView(tab.key)}
                className={`kw-btn kw-btn-small w-full px-2 ${
                  view === tab.key ? '' : 'border-0 bg-transparent text-ink-muted'
                }`}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <SealedNotice id="journal-sealed" />

      {/* Wischen auf dem Inhalt wechselt die Unterreiter (24.10.2026). */}
      <SwipePages
        current={view}
        neighbour={neighbourIn(JOURNAL_VIEWS)}
        onChange={setView}
        render={(key) =>
          key === 'days' ? (
            todayDate ? (
              <JournalDays today={todayDate} onOpen={open} />
            ) : null
          ) : key === 'month' ? (
            <MonthView onOpen={open} />
          ) : key === 'bucket' ? (
            <BucketList onOpen={open} />
          ) : (
            <>
              {child ? null : <WeekReview />}
              {/* Teens (28.10.2026): Erfolge ja, die Woche mit Gewicht und Protein nicht. */}
              {child ? <WeekRings size="large" /> : null}
              {child ? <Achievements /> : null}
              {/* Gewicht seit 20.10.2026 hier statt zwischen den Tagen: ein
              Trend gehört zum Rückblick. Kinder (E16): kein Gewicht. */}
              {todayDate && !child ? <WeightTrendTile today={todayDate} /> : null}
              {todayDate && !child ? <MeasurementTrendTile today={todayDate} /> : null}
              <section aria-labelledby="journal-photos-heading" className="flex flex-col gap-2">
                <h2 id="journal-photos-heading" className="kw-kicker">
                  {t('journal.tabs.photos')}
                </h2>
                <Photos onOpen={open} />
              </section>
            </>
          )
        }
      />
    </div>
  );
}

function Photos({ onOpen }: { onOpen: (date: string) => void }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const photos = useLiveQuery(() => listAllPhotos(db), [], []);

  if (photos.length === 0) {
    return <p className="kw-tile text-sm text-ink-muted">{t('journal.noPhotos')}</p>;
  }

  return (
    <ul className="grid grid-cols-3 gap-2">
      {photos.map(({ photo, date }) => (
        <li key={photo.id}>
          <button
            type="button"
            onClick={() => onOpen(date)}
            aria-label={t('journal.openDay', { day: formatDay(date, 'full', i18n.language) })}
            className="block w-full"
          >
            <PhotoImage photo={photo} />
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Ein offener Tag (24.10.2026): Wischen auf dem Tag blättert zum Tag davor
 * bzw. danach — nie über heute hinaus —, vom linken Rand geht es zurück zur
 * Liste. Die Knöpfe bleiben.
 */
function JournalDayDetail({
  date,
  today,
  onOpen,
  render,
}: {
  date: string;
  today: string | null;
  onOpen: (date: string | null) => void;
  render: (date: string) => React.ReactNode;
}): React.JSX.Element {
  useSwipeBack(() => onOpen(null));
  return (
    <SwipePages
      mode="pager"
      current={date}
      neighbour={(day, dir) => {
        const next = addDays(day as IsoDate, dir);
        return dir === 1 && today && next > today ? null : next;
      }}
      onChange={(day) => onOpen(day)}
      render={render}
    />
  );
}
